/**
 * The *Widget settings* pane the three non-table views share
 * (`specs/M4-V2-kanban.md` §2.5, `specs/M4-V4-calendar.md` §2.3, `specs/M4-V7-gallery.md` §7).
 *
 * monday opens one right-hand panel, ~366 wide, whose fields carry its own `settings_str`
 * keys in their class names (`settings-field-component-name-pivot_by_column`, `…card_columns`,
 * `…display_mode`). Ours keeps those keys as `data-field`, so a Playwright hit-test names the
 * same thing monday does and a reader can line the two up.
 *
 * The kinds declare their fields; this renders them. A new setting is one entry in the kind's
 * array, never a new component.
 */
import {
  ALL_COLUMNS_SELECTION,
  columnSelectionToMonday,
  isColumnSelected,
  isEmptySelection,
  type ColumnSelection,
} from '@elizmonday/shared';
import { Icon, icons, useOverlayDismiss } from '@elizmonday/ui';
import { useState } from 'react';
import { getColumnUi } from '../../board-view/columns/registry';
import './settings-pane.css';

export interface ColumnRef {
  id: string;
  title: string;
  type: string;
}

export type SettingsField =
  | {
      kind: 'select';
      id: string;
      label: string;
      value: string;
      options: { value: string; label: string }[];
      onChange(value: string): void;
    }
  | {
      kind: 'radio';
      id: string;
      label: string;
      value: string;
      options: { value: string; label: string }[];
      onChange(value: string): void;
    }
  | {
      kind: 'toggle';
      id: string;
      label: string;
      value: boolean;
      onChange(value: boolean): void;
    }
  | {
      kind: 'columns';
      id: string;
      label: string;
      columns: ColumnRef[];
      selection: ColumnSelection;
      /**
       * **P-146.** What an **empty** selection means to the view that reads it, because the two
       * halves of this pane used to disagree: the calendar's *Date columns*, the gantt's
       * *Timeline column* and the workload's *Time columns* all fall back to **every** placeable
       * column when nothing is configured (`calendarDateColumnIds`, `barColumnFor`,
       * `workloadDateColumnFor`), while the pane painted every box from
       * `isColumnSelected(EMPTY, id)` = `false` — so the pane said "no date column" over a grid
       * placing two, unticking was a no-op, and ticking one read as the pane deleting a column
       * the user never touched. `'none'` is the kanban's card / cover columns, where an empty
       * selection really does mean nothing (`resolveCardColumns`).
       */
      emptyMeans?: 'all' | 'none';
      onChange(selection: ColumnSelection): void;
    };

export function ViewToolbar({
  title,
  fields,
  testId,
}: {
  title: string;
  fields: readonly SettingsField[];
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  /*
   * P-016: the pane is dismissible. Without this it stayed open for the rest of the screen —
   * every control it covered then answered no tap, which is what produced 50 of the sweep's
   * `not-clickable` rows on seven screens. `useOverlayDismiss` is the shared contract for a
   * surface that renders itself (P-013): Escape closes it and focus goes back to the gear.
   */
  useOverlayDismiss({ open, onClose: () => setOpen(false) });
  return (
    <div className="ez-view-toolbar" data-testid={testId}>
      <button
        type="button"
        className="ez-view-toolbar__gear"
        aria-label={`${title} settings`}
        aria-expanded={open}
        data-testid={`${testId}-settings-button`}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon icon={icons.settings} size={18} ignoreFocusStyle />
      </button>

      {open ? (
        <>
          {/*
           * P-016: below the phone breakpoint the pane is a bottom sheet, and a sheet without a
           * backdrop is what made 50 controls unclickable — the board behind it answered no tap
           * and was not "behind a modal" either. The scrim is `display: none` above 767 px, so
           * the measured desktop pane is untouched; on a phone it dismisses the sheet.
           */}
          <div
            className="ez-view-settings__scrim"
            data-testid={`${testId}-settings-scrim`}
            role="presentation"
            onClick={() => setOpen(false)}
          />
          <aside className="ez-view-settings" data-testid={`${testId}-settings`} aria-label={`${title} settings`}>
            <header className="ez-view-settings__bar">
              <h2 className="ez-view-settings__title">Widget settings</h2>
              {/* Phone-only: the gear that toggles the pane is behind the scrim on a phone. */}
              <button
                type="button"
                className="ez-view-settings__close"
                aria-label={`Close ${title} settings`}
                data-testid={`${testId}-settings-close`}
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </header>
            <div className="ez-view-settings__kind">{title}</div>
            {fields.map((field) => (
              <SettingsFieldRow key={field.id} field={field} testId={testId} />
            ))}
          </aside>
        </>
      ) : null}
    </div>
  );
}

/**
 * **P-146.** The selection the pane paints: the stored one, unless it is empty *and* the kind
 * reads an empty selection as "every column", in which case it is the fallback the view placed.
 * One reading, in one place, for the three kinds that share this field.
 */
export function effectiveSelection(
  field: Extract<SettingsField, { kind: 'columns' }>,
): ColumnSelection {
  if (field.emptyMeans !== 'all') return field.selection;
  return isEmptySelection(field.selection) ? ALL_COLUMNS_SELECTION : field.selection;
}

function SettingsFieldRow({ field, testId }: { field: SettingsField; testId: string }) {
  return (
    <section className="ez-view-settings__field" data-field={field.id} data-testid={`${testId}-field-${field.id}`}>
      <h3 className="ez-view-settings__label">{field.label}</h3>

      {field.kind === 'select' ? (
        <select
          className="ez-view-settings__select"
          value={field.value}
          data-testid={`${testId}-${field.id}-select`}
          aria-label={field.label}
          onChange={(event) => field.onChange(event.target.value)}
        >
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : null}

      {field.kind === 'radio'
        ? field.options.map((option) => (
            <label className="ez-view-settings__row" key={option.value}>
              <input
                type="radio"
                name={`${testId}-${field.id}`}
                checked={field.value === option.value}
                data-testid={`${testId}-${field.id}-${option.value}`}
                onChange={() => field.onChange(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))
        : null}

      {field.kind === 'toggle' ? (
        <label className="ez-view-settings__row">
          <input
            type="checkbox"
            checked={field.value}
            data-testid={`${testId}-${field.id}-toggle`}
            onChange={(event) => field.onChange(event.target.checked)}
          />
          <span>{field.label}</span>
        </label>
      ) : null}

      {field.kind === 'columns' ? (
        <>
          {/*
            P-146: the boxes are painted from — and edited against — the **effective** selection,
            so the pane says what the view did. An unconfigured field whose empty selection the
            view reads as "all" opens with every box ticked, and the gesture that narrows it is
            unticking the others; the raw selection is still what is stored, and the first such
            untick is what turns `{}` into monday's own `{"all":true,"<id>":false}`.
          */}
          <label className="ez-view-settings__row">
            <input
              type="checkbox"
              checked={effectiveSelection(field).all === true}
              data-testid={`${testId}-${field.id}-all`}
              onChange={(event) => field.onChange({ all: event.target.checked, byColumn: {} })}
            />
            <span>All columns for this Board</span>
          </label>
          {field.columns.map((column) => (
            <label className="ez-view-settings__row" key={column.id}>
              <input
                type="checkbox"
                checked={isColumnSelected(effectiveSelection(field), column.id)}
                data-testid={`${testId}-${field.id}-${column.id}`}
                onChange={(event) =>
                  field.onChange({
                    all: effectiveSelection(field).all,
                    byColumn: {
                      ...effectiveSelection(field).byColumn,
                      [column.id]: event.target.checked,
                    },
                  })
                }
              />
              <Icon icon={icons[getColumnUi(column.type).icon]} size={16} ignoreFocusStyle />
              <span>{column.title}</span>
            </label>
          ))}
          {/* The monday shape, so a reader can compare the pane with `settings_str`. Still the
              **stored** selection: what the round trip has to preserve is what monday wrote. */}
          <span hidden data-testid={`${testId}-${field.id}-monday`}>
            {JSON.stringify(columnSelectionToMonday(field.selection))}
          </span>
        </>
      ) : null}
    </section>
  );
}
